import { Dispatch, SetStateAction, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { errorHandleMiddleware, processFile } from '@entities/attachment/lib';
import {
    TAttachmentDTO,
    TAttachmentDTOCamelCased,
    TAttachmentOnClient,
} from '@entities/attachment/model/types';
import { generateId } from '@shared/helpers';

interface IUseFilesUploader {
    attachments: (TAttachmentOnClient | TAttachmentDTOCamelCased)[];
    setAttachments: Dispatch<SetStateAction<(TAttachmentOnClient | TAttachmentDTOCamelCased)[]>>;
    multipleUpload?: boolean;
    maxAttachmentSizeInMb?: number;
    maxOverallSizeInMb?: number;
    replaceUploadFormats?: string[];
}

export const useFilesUploader = ({
    attachments,
    setAttachments,
    multipleUpload = false,
    maxAttachmentSizeInMb = 1000,
    maxOverallSizeInMb = 10000,
    replaceUploadFormats,
}: IUseFilesUploader) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onAddFiles = async (files: File[]) => {
        const tempAttachments: TAttachmentOnClient[] = files.map((file) => ({
            id: generateId(true) as number,
            file,
            isLoading: true,
        }));
        let formattedFiles: (TAttachmentOnClient | TAttachmentDTOCamelCased)[] = [
            ...tempAttachments,
        ];
        if (multipleUpload) {
            formattedFiles = [...attachments, ...tempAttachments];
            setAttachments(formattedFiles);
        }

        const processedFiles = await Promise.all(
            files.map((file) => {
                const temp = tempAttachments.find((ta) => ta.file.name === file.name);
                const fileId = temp?.id ?? (generateId(true) as number);
                return processFile(file, fileId);
            })
        );

        const finalAttachments = formattedFiles.map((attachment) => {
            const matched = processedFiles.find((pf) => pf.id === attachment.id);
            return matched || attachment;
        });

        setAttachments(finalAttachments);
    };

    const onClickInputRef = () => {
        fileInputRef.current?.click();
    };

    const uploadFilesHandler = errorHandleMiddleware({
        onAddFiles,
        alreadyAttachedLength: attachments.length || 0,
        t,
        maxAttachmentSizeInMb,
        maxOverallSizeInMb,
        replaceUploadFormats,
        multiple: multipleUpload,
    });

    const onDeleteAttachment = (deletedId: TAttachmentOnClient['id'] | TAttachmentDTO['id']) => {
        const filtered = attachments.filter((attachment) => attachment.id !== deletedId);
        setAttachments(filtered);
    };

    return {
        fileInputRef,
        onUploadHandler: uploadFilesHandler,
        onDeleteAttachment,
        t,
        onClickInputRef,
    };
};
